<template>
  <TableBodyRow
    :id="`drills_table_row_${index}`"
    ref="cont"
    class="gw_table_body_row_hoverable drills_table_row"
    :thead="thead"
    :class="[
      {
        drillrow_dragged: draggedRows.some(i => (i ? i.id === item.id : false)),
      },
    ]"
    @click="e => $emit('click', e)"
  >
    <TableBodyCell
      style="--max-width-td: 6rem; --min-width-td: 6rem; --padding-td: 0; justify-content: center"
      @click.stop
    >
      <div
        class="d-flex"
        style="gap: 0.5rem"
      >
        <Checkbox
          v-if="hasDragdrop"
          :class="dragDisabled ? 'clr-black-4 icon_btn_disabled cursor-not-allowed' : 'cursor-grab'"
          class="csst csst_nm flgmfslctr_title_controls_item"
          style="
            --icon-size: 1.25rem;
            --clr-disabled: var(--clr-grey-2);
            --clr-enabled: var(--clr-primary);
          "
          :icons="['mdi-drag-vertical', 'mdi-drag-vertical']"
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <template #content>
            <div
              class="csst_content clr-white text-nowrap"
              :style="dragDisabled ? `background:var(--clr-error)` : ''"
              style="line-height: 1.2; left: calc(100% + 0.5rem)"
              v-html="
                dragDisabled
                  ? $t('tooltips.drag_drop_disable_enable_sort')
                  : $t('tooltips.drill_drag_drop_change_order')
              "
            ></div>
          </template>
        </Checkbox>
        <Checkbox
          class="csst csst_nm flgmfslctr_title_controls_item"
          style="
            --icon-size: 1.25rem;
            --clr-disabled: var(--clr-grey-2);
            --clr-enabled: var(--clr-primary);
          "
          :value="selected"
          @click="$emit('toggle:selected')"
        >
          <template #content>
            <div
              class="csst_content clr-white"
              style="line-height: 1.2; left: calc(100% + 0.5rem)"
              v-html="$t('common.select')"
            />
          </template>
        </Checkbox>
        <Checkbox
          class="csst csst_nm flgmfslctr_title_controls_item"
          :value="item.favorite"
          style="
            --icon-size: 1.25rem;
            --clr-disabled: var(--clr-grey-2);
            --clr-enabled: var(--clr-yellow);
          "
          :icons="['mdi-star-outline', 'mdi-star']"
          @click="$emit('toggle:favorite')"
        >
          <template #content>
            <div
              class="csst_content clr-white text-nowrap"
              style="line-height: 1.2; left: calc(100% + 0.5rem)"
              v-html="$t(`common.${item.favorite ? 'starred' : 'not_starred'}`)"
            />
          </template>
        </Checkbox>
      </div>
    </TableBodyCell>

    <TableBodyCell thKey="name">
      <div
        class="truncate"
        :title="item.name"
      >
        {{ item.name }}
      </div>
    </TableBodyCell>
    <TableBodyCell
      v-if="myDrillsVisible"
      thKey="tags"
    >
      <GwMenu
        placement="bottom-start"
        :flOptions="{ offset: 0, padding: 0 }"
        style="max-width: 100%"
        :ignoreDataGwmenu="true"
        @click.stop
      >
        <template #opener>
          <TagsTableCellList
            class="gw_btn_hover_back"
            :tags="item.tags"
            style="max-width: 100%"
          />
        </template>
        <template #default>
          <TagsManager
            data-gwmenu="noclose"
            style="box-shadow: var(--menu-shadow)"
            :inputAutofocus="!mobile"
            :tagIds="item.tags.map(t => t.id)"
            :tagType="tagType"
            :allTags="allTags"
            :hasAnyAll="false"
            @click:tag="tag => $emit('click:tag_item', { tag, tagItems: item.tags })"
          />
        </template>
      </GwMenu>
    </TableBodyCell>
    <TableBodyCell
      v-else
      thKey="tags"
    >
      <TagsTableCellList
        class="gw_btn_hover_back"
        :tags="item.tags"
        style="max-width: 100%"
        :tagsPlaceholder="' '"
      />
    </TableBodyCell>
    <TableBodyCell thKey="l_gmf_format">
      {{ item.l_gmf_format === 'Custom' ? 'Custom' : $t(`gametypes.groups.${item.l_gmf_format}`) }}
    </TableBodyCell>
    <TableBodyCell thKey="total_hands">
      <span v-if="item.totals">{{ item.totals.total_hands }} </span>
      <span
        v-else
        class="clr-grey"
      >
        -
      </span>
    </TableBodyCell>
    <TableBodyCell thKey="gto_score_avg">
      <span
        v-if="item.totals"
        :style="`color:${getGtoScoreGroup(item.totals.gto_score_avg).clr}`"
      >
        {{ numFixed(item.totals.gto_score_avg * 100, 2) }}
      </span>
      <span
        v-else
        class="clr-grey"
      >
        -
      </span>
    </TableBodyCell>

    <TableBodyCell
      thKey="opener"
      class="f-center"
      style="--padding-td: 0 0.25rem; flex: 1; max-width: unset; min-width: unset; order: 99"
    >
      <DrillTableRowControls
        class="ml-auto gw_hover_half-opacity"
        :namespace="namespace"
        :item="item"
      />
      <div class="weight_500 gw_btn text-medium gw_hover_half-opacity csst csst_nm ml-2">
        <div class="mdi mdi-chevron-double-right mr-1"></div>

        {{ $t('common.stats') }}
        <div
          class="csst_content clr-white text-nowrap"
          style="
            position: absolute;
            max-width: clamp(12rem, 36rem, 96vw);
            left: 0;
            transform: translate(calc(-100% - 2px), 0);
          "
          v-html="$t('tooltips.see_performance')"
        ></div>
      </div>
    </TableBodyCell>
  </TableBodyRow>
</template>

<script>
import TagsTableCellList from '@/components/TagsManager/TagsTableCellList'
import GwMenu from '@/components/lib/GwMenu'
import Checkbox from '@/components/Common/Checkbox/Checkbox'
import TagsManager from '@/components/TagsManager/TagsManager'
import TableBodyRow from '@/components/DataTables/v2/TableBodyRow'
import TableBodyCell from '@/components/DataTables/v2/TableBodyCell'
import table_mix from '@/components/DataTables/table_mix'
// import { throttle } from 'lodash'
import DrillTableRowControls from '@/components/DataTables/v2/tables/DrillsTable/DrillTableRowControls'

export default {
  name: 'DrillsTableRow',
  components: {
    TableBodyRow,
    TableBodyCell,
    DrillTableRowControls,
    TagsTableCellList,
    GwMenu,
    Checkbox,
    TagsManager,
  },
  mixins: [table_mix],
  props: {
    namespace: { String, required: true },

    item: Object,
    index: Number,
    thead: Object,
    tagType: String,
    allTags: Array,
    myDrillsVisible: Boolean,

    hasDragdrop: Boolean,
    hasTagEdit: Boolean,
    selected: Boolean,
    dragDisabled: Boolean,
    draggedRows: Array,
  },
  emits: [
    'click',
    'toggle:selected',
    'toggle:favorite',
    'click:tags',
    'drag:start',
    'click:tag_item',
  ],
  data() {
    return {
      isDraggable: false,
      dragSurface: '',
      dragEl: false,
    }
  },
  methods: {
    startDrag(e) {
      if (this.dragDisabled) return
      this.$emit('drag:start', e)
    },
  },
  computed: {
    mobile() {
      return this.$store.getters.globals_mobile
    },
  },
}
</script>

<style lang="scss" scoped>
.drillrow {
  &_dragged {
    pointer-events: none;
    --clr-back-row-hover: var(--clr-black-0);
    --clr-back-row: var(--clr-black-0);
  }
}
</style>
